//@ts-nocheck
import React, { forwardRef } from 'react';
import type { UploadFile, UploadProps } from 'antd';
import { message, Spin, Upload } from 'antd';
import { Gallery, DirectboxSend } from 'iconsax-react';
import UploadFileItem from './uploadFileItem';
import { AllowedFileTypes } from '@/components/FormField/interfaces';
import Label from './Label';
import HelperText from './HelperText';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

interface Props extends UploadProps {
  id?: string;
  name?: string;
  fileList?: UploadFile[];
  allowedTypes?: AllowedFileTypes[];
  uploadHints?: string[];
  maxLengthFileName?: number;
  onChangeFile?: (newFileList: UploadFile[]) => void;
  uploadFileRequest?: (newFileList: UploadFile[]) => void;
  onDeleteDocument?: (id?: string | number) => void;
  disabled?: boolean;
  maxFileSize?: number;
  required?: boolean;
  label?: string;
  labelHelperText?: string;
  helperText?: string;
  status?: 'error' | 'success';
  isProfilePic?: boolean;
  uploadDescription?: string;
  optional?: boolean;
  loading?: boolean;
}

const Uploader = forwardRef<HTMLInputElement, Props>(
  (
    {
      fileList,
      onChangeFile,
      uploadFileRequest,
      onDeleteDocument,
      multiple = true,
      allowedTypes,
      uploadHints,
      maxLengthFileName,
      action,
      labelHelperText,
      required,
      label,
      id,
      maxFileSize = 5,
      helperText,
      status,
      isProfilePic,
      uploadDescription,
      maxCount,
      optional,
      loading = false,
      ...rest
    }: Props,
    ref
  ) => {
    const { t } = useTranslation();

    // Handle internal file list changes and pass it to the parent
    const handleChange = ({
      fileList: newFileList,
    }: {
      file?: UploadFile;
      fileList: UploadFile[];
    }) => {
      let filteredFileList = newFileList;
      if (maxCount && newFileList.length > maxCount) {
        filteredFileList = newFileList.slice(0, maxCount);
        message.error({
          content: t('components.validations.maxFielsLength', {
            num: maxCount,
            defaultValue: `You can upload up to ${maxCount} files only`,
          }),
          key: 'maxCountError',
          duration: 5,
        });
      }

      if (!multiple || maxCount === 1) {
        onChangeFile?.(filteredFileList.slice(-1));
      } else {
        onChangeFile?.(filteredFileList);
      }
    };
    const handleUpload = () => {
      // you can use `fetch` or `axios` to send files to your backend or cloud storage
      uploadFileRequest?.(fileList);
    };

    const beforeUpload = (file: File) => {
      const isValidSize = file.size / 1024 / 1024 <= maxFileSize; // Convert bytes to MB
      if (!isValidSize) {
        message.error({
          content: t('components.validations.maxFileSize', {
            size: maxFileSize,
          }),
          key: 'maxFileSizeError',
          duration: 5,
        });
        return Upload.LIST_IGNORE;
      }
      if (allowedTypes.includes('*/*')) return true;
      if (!allowedTypes.includes(file.type as AllowedFileTypes)) {
        message.error({
          content: `${file.name} ${t('validations.thisFielTypeNotAcceptable')}`,
          key: `invalidType_${file.name}`,
          duration: 5,
        });
        return Upload.LIST_IGNORE; // Prevent the file from being added
      }

      return true; // Allow the file to be added
    };

    return (
      <Spin spinning={loading}>
        <div>
          {label && (
            <Label
              text={label}
              htmlFor={id}
              optional={optional}
              labelHelperText={
                labelHelperText ||
                (maxCount
                  ? `${fileList?.length || 0}/${maxCount} ${t('components.documents')}`
                  : undefined)
              }
              required={required}
            />
          )}
          <Dragger
            className={`block ${
              isProfilePic
                ? '!w-32 !h-32 [&_.ant-upload-drag]:!rounded-full [&_.ant-upload-drag]:!border-gray-300 dark:[&_.ant-upload-drag]:!border-gray-700 [&_.ant-upload-drag]:!bg-gray-50 dark:[&_.ant-upload-drag]:!bg-gray-800'
                : '[&_.ant-upload-drag]:!bg-soft-background dark:[&_.ant-upload-drag]:!bg-dark-surface-secondary [&_.ant-upload-drag]:!border-2 [&_.ant-upload-drag]:!border-dashed [&_.ant-upload-drag]:!border-[#E7E7E9] dark:[&_.ant-upload-drag]:!border-neutral-700 [&_.ant-upload-drag]:!rounded-xl [&_.ant-upload-btn]:!p-0'
            }`}
            ref={ref}
            id={id}
            customRequest={action ? undefined : handleUpload}
            onChange={handleChange}
            itemRender={(originNode, file, fileList: UploadFile[]) => (
              <UploadFileItem
                status={status}
                file={file}
                fileList={fileList}
                setFileList={handleChange}
                maxLengthFileName={maxLengthFileName}
                onDeleteDocument={onDeleteDocument}
              />
            )}
            action={action}
            beforeUpload={beforeUpload}
            fileList={fileList}
            multiple={multiple}
            status={status}
            accept={allowedTypes?.join(',')}
            {...rest}
          >
            {isProfilePic ? (
              <div className="flex flex-col items-center justify-center gap-2 relative h-full">
                <div className="flex items-center justify-center w-full h-full text-gray-400">
                  <div className="relative">
                    <Gallery className="opacity-20 -translate-x-1 -rotate-[10deg]" />

                    <Gallery
                      className="absolute top-0 translate-x-1 rotate-[30deg] opacity-70"
                      variant="Bold"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex relative flex-row items-center justify-start gap-x-4 text-start w-full">
                <div className="ant-upload-drag-icon flex justify-center flex-shrink-0">
                  <DirectboxSend
                    variant="Linear"
                    size="24"
                    className="text-[#090909] dark:text-neutral-400"
                  />
                </div>
                <div className="flex flex-col text-start">
                  <p className="!text-[#090909] dark:text-dark-text-primary !font-semibold !text-base text-start">
                    {uploadDescription || t('components.uploaderDesc')}
                  </p>
                  <p className="!text-[#777777] !text-xs !font-normal !mt-1 flex items-center flex-wrap gap-1 text-start">
                    {uploadHints?.map((hint, index) => (
                      <span key={index}>{hint}</span>
                    ))}
                  </p>
                </div>
              </div>
            )}
          </Dragger>
          {helperText && <HelperText text={helperText} status={status} />}
        </div>
      </Spin>
    );
  }
);

Uploader.displayName = 'Uploader';

export default Uploader;
